import { useEffect, useState, useRef } from "react";
import { Plus, Edit, Trash2, Tag, Loader2, AlertCircle } from "lucide-react";
import API from "../../api/api";
import BatchActionBar from "../../components/BatchActionBar";

const emptyForm = { topTitle: "", flat: "", description: "", year: "", coins: "", rates: "", status: "Active" };

export default function Offers() {
    const [offers, setOffers] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const formRef = useRef(null);
    const titleInputRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    // Multi-delete state
    const [selectedIds, setSelectedIds] = useState([]);
    const [isBatchDeleting, setIsBatchDeleting] = useState(false);

    const fetchOffers = async () => {
        try {
            setLoading(true);
            setError("");
            const { data } = await API.get("/admin/offers");
            if (!data?.success) throw new Error(data?.message || "Failed to load offers");
            setOffers(data.offer || []);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Failed to load offers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOffers();
    }, []);

    const handleToggleSelectAll = () => {
        if (selectedIds.length === offers.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(offers.map((o) => o._id));
        }
    };

    const handleToggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleBatchDelete = async () => {
        try {
            setIsBatchDeleting(true);
            const { data } = await API.post("/admin/batch-delete", {
                entity: "offers",
                ids: selectedIds,
            });
            if (data?.success) {
                setSelectedIds([]);
                fetchOffers();
            }
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Batch delete failed");
        } finally {
            setIsBatchDeleting(false);
        }
    };

    const resetForm = () => {
        setForm(emptyForm);
        setEditingId(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError("");
            if (editingId) {
                await API.put(`/admin/offers/${editingId}`, form);
            } else {
                await API.post("/admin/offers", form);
            }
            resetForm();
            fetchOffers();
        } catch (err) {
            setError(err?.response?.data?.message || err.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (offer) => {
        setEditingId(offer._id);
        setForm({
            topTitle: offer.topTitle || "",
            flat: offer.flat || "",
            description: offer.description || "",
            year: offer.year || "",
            coins: offer.coins || "",
            rates: offer.rates || offer.rate || "",
            status: offer.status || "Active",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
        setTimeout(() => {
            titleInputRef.current?.focus({ preventScroll: true });
        }, 150);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this offer?")) return;
        try {
            await API.delete(`/admin/offers/${id}`);
            setOffers((prev) => prev.filter((o) => o._id !== id));
            setSelectedIds((prev) => prev.filter((i) => i !== id));
        } catch (err) {
            setError(err?.response?.data?.message || "Delete failed");
        }
    };

    return (
        <div className="space-y-6 lg:pl-5 xl:pl-7 2xl:pl-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Manage Offers</h1>
            </div>

            {error && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300">
                    <AlertCircle className="w-4 h-4" />
                    <span>{error}</span>
                </div>
            )}

            <form
                ref={formRef}
                onSubmit={handleSubmit}
                className={`bg-white dark:bg-slate-800 rounded-xl border p-4 space-y-3 transition-all duration-300 scroll-mt-24 ${
                    editingId
                        ? "border-pink-500 ring-2 ring-pink-500/20 shadow-pink-500/10"
                        : "border-gray-200 dark:border-slate-700"
                }`}
            >
                {editingId && (
                    <div className="flex items-center justify-between pb-2 border-b border-gray-100 dark:border-slate-700/60">
                        <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-pink-500 animate-pulse" />
                            <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">
                                Editing Offer: {form.topTitle || "Untitled"}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={resetForm}
                            className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
                        >
                            Cancel Edit
                        </button>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Title
                        <input
                            ref={titleInputRef}
                            type="text"
                            value={form.topTitle}
                            onChange={(e) => setForm({ ...form, topTitle: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </label>
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Flat Discount Text
                        <input
                            type="text"
                            value={form.flat}
                            onChange={(e) => setForm({ ...form, flat: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </label>
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Validity / Year
                        <input
                            type="text"
                            value={form.year}
                            onChange={(e) => setForm({ ...form, year: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </label>
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Coins
                        <input
                            type="text"
                            value={form.coins}
                            onChange={(e) => setForm({ ...form, coins: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </label>
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Rate / Price
                        <input
                            type="text"
                            value={form.rates}
                            onChange={(e) => setForm({ ...form, rates: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        />
                    </label>
                    <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200">
                        Status
                        <select
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                        >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                        </select>
                    </label>
                </div>
                <label className="space-y-1 text-sm text-gray-700 dark:text-gray-200 block">
                    Description
                    <textarea
                        rows={3}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-pink-500"
                    />
                </label>
                <div className="flex items-center gap-3">
                    <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 disabled:opacity-60 cursor-pointer">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        {editingId ? "Update Offer" : "Create Offer"}
                    </button>
                    {editingId && (
                        <button type="button" onClick={resetForm} className="text-sm text-gray-500 hover:text-gray-700 cursor-pointer">Cancel edit</button>
                    )}
                </div>
            </form>

            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-slate-700">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-slate-700/50">
                                <th className="px-3 xl:px-4 py-3.5 xl:py-4 w-12 text-center">
                                    <input
                                        type="checkbox"
                                        checked={offers.length > 0 && selectedIds.length === offers.length}
                                        onChange={handleToggleSelectAll}
                                        className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                    />
                                </th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Title</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Coins</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Rate</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                <th className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                            {loading ? (
                                <tr><td colSpan={6} className="py-8 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading...</td></tr>
                            ) : offers.length === 0 ? (
                                <tr><td colSpan={6} className="py-6 text-center text-gray-500">No offers yet</td></tr>
                            ) : (
                                offers.map((offer) => {
                                    const isSelected = selectedIds.includes(offer._id);
                                    return (
                                    <tr key={offer._id} className={`transition-colors ${isSelected ? "bg-pink-50/70 dark:bg-pink-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
                                        <td className="px-3 xl:px-4 py-3.5 xl:py-4 text-center">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleSelectRow(offer._id)}
                                                className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                            />
                                        </td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-lg flex items-center justify-center">
                                                    <Tag className="w-5 h-5 text-white" />
                                                </div>
                                                <div>
                                                    <p className="text-sm text-gray-800 dark:text-gray-200 font-medium">{offer.topTitle || "-"}</p>
                                                    <p className="text-xs text-gray-500">{offer.description}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-gray-800">{offer.coins || "-"}</td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 text-sm text-cyan-500 font-bold">{offer.rates || "-"}</td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4">
                                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${offer.status === "Active"
                                                ? "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400"
                                                : "bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400"}`}>
                                                {offer.status}
                                            </span>
                                        </td>
                                        <td className="px-3.5 xl:px-4 2xl:px-6 py-3.5 xl:py-4 space-x-2">
                                            <button onClick={() => handleEdit(offer)} className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-300">
                                                <Edit className="w-3.5 h-3.5" /> Edit
                                            </button>
                                            <button onClick={() => handleDelete(offer._id)} className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded-lg bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300">
                                                <Trash2 className="w-3.5 h-3.5" /> Delete
                                            </button>
                                        </td>
                                    </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Floating Multi-Delete Bar */}
            <BatchActionBar
                selectedCount={selectedIds.length}
                totalCount={offers.length}
                onClearSelection={() => setSelectedIds([])}
                onSelectAll={handleToggleSelectAll}
                onDeleteSelected={handleBatchDelete}
                isDeleting={isBatchDeleting}
                entityName="offers"
            />
        </div>
    );
}
